import difference from 'lodash/difference';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
// modules
import SearchService from 'modules/Search';
import FormatService from 'modules/Format';
import LocaleService from 'modules/Localization';
import { broker } from 'modules/TradingChart/consts';
// types
import {
  IWatchListApi,
} from '../TradingChart/components/TradingView/sources/charting_library.min';
import {
  NotificationType,
} from '../TradingChart/components/TradingView/sources/charting_library';
import WatchlistFetchService from './WatchlistFetchService';
// constants
import {
  ACTIVE_WATCHLIST_ID,
  DEFAULT_WATCHLIST,
  WatchlistErrorCodes,
  WatchlistNumberTypes,
} from './constants';

class WatchlistService {
  tvApiInstance: IWatchListApi;
  watchlists: Record<string, WatchlistItem> = {};
  watchListForLocalDeleting = '';
  localDeletingWatchListInProgress = false;
  isWatchlistCanBeRename = true;
  watchlistAddedTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(tvInstance: IWatchListApi) {
    this.tvApiInstance = tvInstance;
  }

  subscribeEvent = () => {
    this.tvApiInstance.onListChanged().subscribe(this, this.onListChanged);
    this.tvApiInstance.onListRemoved().subscribe(this, this.onListRemoved);
    this.tvApiInstance.onListRenamed().subscribe(this, this.onListRenamed);
    this.tvApiInstance.onListAdded().subscribe(this, this.onListAdded);
    this.tvApiInstance
      .onActiveListChanged()
      .subscribe(this, this.onActiveListChanged);
  };

  getWatchlistItems = async (watchlistId: string) => {
    const response = await WatchlistFetchService.getWatchlistById(watchlistId);

    const instruments = response?.data?.instruments ?? [];

    const watchlistItemExchangeTokens = await Promise.all(
      instruments.map(async instrument => {
        const searchData = await SearchService.searchByInstrumentKey(
          instrument.ik,
        );
        return searchData ? searchData.exchangeToken : null;
      }),
    );

    const validWatchlistExchangeTokens = watchlistItemExchangeTokens?.filter(
      exchangeToken => exchangeToken !== null,
    );

    const watchlistTvExchangeTokens = await this.formatExchangeTokenToScripName(
      validWatchlistExchangeTokens as string[],
    );

    return watchlistTvExchangeTokens ?? [];
  };

  fetchWatchlists = async () => {
    const response = await WatchlistFetchService.getAllLists();

    if (response.success) {
      const { data } = response;
      const userWatchlists = data?.watchlists?.filter(
        ({ type }) => type === WatchlistNumberTypes.USER_LIST,
      );
      const defaultWatchlistId = this.tvApiInstance.getActiveListId() || '';

      const activeWatchlist = window.localStorage.getItem(ACTIVE_WATCHLIST_ID);
      const parsedActiveWatchlist: ActiveWatchlist = JSON.parse(
        activeWatchlist || '{}',
      );
      let activeWatchlistId = 0;
      let activeWatchlistItems: string[] = [];

      const findActiveWatchlistIndex = userWatchlists.findIndex(
        ({ name }) =>
          name.toLowerCase() ===
          parsedActiveWatchlist?.title?.toLowerCase(),
      );

      if (findActiveWatchlistIndex !== -1) {
        activeWatchlistId = findActiveWatchlistIndex;
      }

      activeWatchlistItems = userWatchlists?.length > 0
        ? await this.getWatchlistItems(userWatchlists[activeWatchlistId].id)
        : [];

      userWatchlists.forEach(({ id, name, type }, index) => {
        const isActiveWatchlist = index === activeWatchlistId;
        this.tvApiInstance.saveList({
          id: `${index}`,
          title: name,
          symbols: isActiveWatchlist ? activeWatchlistItems : [],
        });
        this.watchlists[`${index}`] = {
          items: isActiveWatchlist ? activeWatchlistItems : [],
          name,
          type,
          watchlistId: id,
          fetchedItems: isActiveWatchlist,
        };
      });

      if (
        activeWatchlist !== null &&
        this.watchlists[`${activeWatchlistId}`]
      ) {
        this.tvApiInstance.setActiveList(`${activeWatchlistId}`);
      } else {
        const firstWatchlist = userWatchlists?.[0];
        window.localStorage.setItem(
          ACTIVE_WATCHLIST_ID,
          JSON.stringify({
            id: '0',
            title: firstWatchlist?.name,
          }),
        );
      }

      if (userWatchlists?.length === 0) {
        const resp = await WatchlistFetchService.createWatchlist(
          'Default watchlist',
          [],
        );

        if (resp.success) {
          this.tvApiInstance.saveList(DEFAULT_WATCHLIST);
        }
      }

      this.tvApiInstance.deleteList(defaultWatchlistId);
    }

    this.subscribeEvent();
  };

  private updateListExcludingSymbols(
    listId: string,
    scripSymbolsToRemove: string[],
  ) {
    const watchlists = this.tvApiInstance.getAllLists() || {};
    const { symbols: tvWatchlistItems } = watchlists[listId];

    this.tvApiInstance.updateList(
      listId,
      tvWatchlistItems.filter(item => !scripSymbolsToRemove.includes(item)),
    );
  }

  private showWatchlistResponseError(response: ServerErrorResponse) {
    if (broker.instance) {
      broker.instance.showNotification(
        LocaleService.translate('WATCHLIST'),
        response.error?.message ||
          LocaleService.translate('UNABLE_TO_PERFORM_ACTION_TRY_AGAIN'),
        NotificationType.Error,
      );
    }
  }

  listChanged = debounce(async (listId: string) => {
    if (this.localDeletingWatchListInProgress) {
      this.localDeletingWatchListInProgress = false;
      return;
    }

    const watchlists = this.tvApiInstance.getAllLists() || {};

    const { title, symbols: tvWatchlistItems } = watchlists[listId];

    const localWatchlistItems = cloneDeep(this.watchlists[listId].items);
    // Here we compare tvWatchlist(from TV lib) with localWatchlists(from BE), for receiving scrips for add
    const scripsForAdd = difference(tvWatchlistItems, localWatchlistItems);
    // Here we compare localWatchlists(from BE) with tvWatchlist(from TV lib), for receiving scrips for delete
    const scripsForDelete = difference(localWatchlistItems, tvWatchlistItems);

    if (tvWatchlistItems.length > 100) {
      this.updateListExcludingSymbols(listId, scripsForAdd);

      return;
    }

    if (scripsForAdd.length) {
      const formattedTvWatchlistItems = scripsForAdd.map(item =>
        FormatService.decodeCustomTvExchangeToken(item),
      );

      const response = await WatchlistFetchService.updateWatchlist(
        title,
        formattedTvWatchlistItems,
        'POST',
      );

      if (response.success) {
        this.watchlists[listId].items = tvWatchlistItems;
      } else {
        // need to add this condition because we can't check scrip existance
        // in watchlist before it added by TradingView application
        if (broker.instance) {
          this.showWatchlistResponseError(response);

          if (
            (response.error?.code as WatchlistErrorCodes) ===
            WatchlistErrorCodes.USER_WL_INSTRUMENT_LIMIT_BREACH
          ) {
            this.localDeletingWatchListInProgress = true;
            this.updateListExcludingSymbols(listId, scripsForAdd);
          }
        }

        this.watchlists[listId].items = localWatchlistItems;
      }
    }

    if (scripsForDelete.length) {
      const formattedTvWatchlistItems = scripsForDelete.map(item =>
        FormatService.decodeCustomTvExchangeToken(item),
      );

      const response = await WatchlistFetchService.updateWatchlist(
        title,
        formattedTvWatchlistItems,
        'PATCH',
      );

      if (response.success) {
        this.watchlists[listId].items = tvWatchlistItems;
      } else {
        this.showWatchlistResponseError(response);

        this.watchlists[listId].items = localWatchlistItems;
      }
    }

    if (!scripsForDelete.length && !scripsForAdd.length) {
      // If watchlist items are equal, we don't need to update it
      const isWatchlistItemsEqual = isEqual(
        localWatchlistItems,
        tvWatchlistItems,
      );
      if (isWatchlistItemsEqual) {
        return;
      }

      const formattedTvWatchlistItems = tvWatchlistItems.map(item =>
        FormatService.decodeCustomTvExchangeToken(item),
      );

      const response = await WatchlistFetchService.updateWatchlist(
        title,
        formattedTvWatchlistItems,
        'PUT',
      );

      if (response.success) {
        this.watchlists[listId].items = tvWatchlistItems;
      } else {
        this.showWatchlistResponseError(response);

        this.watchlists[listId].items = localWatchlistItems;
      }
    }
  }, 500);

  // DONE
  onListChanged = (listId: string) => {
    const watchlists = this.tvApiInstance.getAllLists() || {};

    const { symbols: tvWatchlistItems } = watchlists[listId];

    if (tvWatchlistItems.length > 100) {
      if (broker.instance) {
        broker.instance.showWatchlistMaxScripsValidationModal();
      }
    }

    this.listChanged(listId);
  };

  checkIfWatchlistNameValid = (
    title: string,
    watchlistId: string,
    rename?: boolean,
  ) => {
    let error: TranslationKey | '' = '';

    if (/[^a-zA-Z0-9& ]+/g.test(title)) {
      error = 'WATCHLIST_NEW_LIST_SPECIAL_CHAR_ERROR';
    } else if (title.length > 40) {
      error = 'WATCHLIST_NEW_LIST_LENGTH_ERROR';
    }

    if (error) {
      if (!rename) {
        this.watchListForLocalDeleting = watchlistId;
        this.tvApiInstance.deleteList(watchlistId);
      }

      if (broker.instance) {
        broker.instance.showNotification(
          LocaleService.translate('ADD_WATCHLIST'),
          LocaleService.translate(error),
          NotificationType.Error,
        );
      }
      return false;
    }

    return true;
  };

  // DONE
  onListAdded(watchlistId: string, symbols: string[]) {
    if (this.watchlistAddedTimeout) {
      clearTimeout(this.watchlistAddedTimeout);
    }
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.watchlistAddedTimeout = setTimeout(async () => {
      const watchlist = this.tvApiInstance.getAllLists() || {};

      if (watchlist[watchlistId]) {
        const newWatchlist = watchlist[watchlistId];

        const { title } = newWatchlist;

        if (!this.checkIfWatchlistNameValid(title, watchlistId)) return;

        const items = symbols.map(item =>
          FormatService.decodeCustomTvExchangeToken(item),
        );

        const response = await WatchlistFetchService.createWatchlist(
          title,
          items,
        );

        if (response.success) {
          const upperWatchlist = title.charAt(0).toUpperCase() + title.slice(1);
          const nextId = Object.keys(this.watchlists).length;

          this.watchlists = {
            ...this.watchlists,
            [watchlistId]: {
              name: upperWatchlist,
              items: newWatchlist.symbols,
              type: WatchlistNumberTypes.USER_LIST,
            },
          };

          window.localStorage.setItem(
            ACTIVE_WATCHLIST_ID,
            JSON.stringify({ id: String(nextId), title: upperWatchlist }),
          );
          window.localStorage.setItem(
            `tradingview.savedwatch.${nextId}`,
            JSON.stringify({
              title: upperWatchlist,
              symbols: newWatchlist.symbols,
            }),
          );
        } else if (broker.instance) {
          this.watchListForLocalDeleting = watchlistId;
          this.tvApiInstance.deleteList(watchlistId);

          broker.instance.showNotification(
            LocaleService.translate('ADD_WATCHLIST'),
            response.error?.message ||
              LocaleService.translate('WATCHLIST_SOMETHING_WENT_WRONG_SUBTEXT'),
            NotificationType.Error,
          );
        }
      }
    }, 0);
  }

  // DONE
  onListRemoved(watchlistId: string) {
    const watchlist = this.tvApiInstance.getAllLists() || {};
    const deleteLocalWatchlist = this.watchListForLocalDeleting === watchlistId;

    if (watchlist[watchlistId] && !deleteLocalWatchlist) {
      this.watchListForLocalDeleting = '';

      const request = async () => {
        const response = await WatchlistFetchService.deleteWatchlist(
          watchlist[watchlistId].title,
          '/user',
          'DELETE_WATCHLIST',
        );

        if (response.success) {
          delete this.watchlists[watchlistId];
        }
      };

      request();
    }
  }

  // DONE
  onListRenamed(listId: string, oldName: string, newName: string) {
    if (!this.checkIfWatchlistNameValid(newName, listId, true)) {
      this.isWatchlistCanBeRename = false;
      this.tvApiInstance.renameList(listId, oldName);
      this.isWatchlistCanBeRename = true;
      return;
    }

    if (this.isWatchlistCanBeRename) {
      const watchlists = Object.values(this.watchlists);
      const isDuplicateName = watchlists.find(
        ({ name }) => name.toLowerCase() === newName.toLowerCase(),
      );

      if (!isDuplicateName) {
        WatchlistFetchService.renameWatchlist(oldName, newName);
      }

      if (broker.instance && isDuplicateName) {
        this.isWatchlistCanBeRename = false;
        this.tvApiInstance.renameList(listId, oldName);

        broker.instance.showNotification(
          LocaleService.translate('DUPLICATE_NAME'),
          LocaleService.translate('WATCHLIST_DUBLICATE_NAME_ERROR'),
          NotificationType.Error,
        );
        this.isWatchlistCanBeRename = true;
      }
    }
  }

  // DONE
  onActiveListChanged = () => {
    const activeListId = this.tvApiInstance.getActiveListId() || '';
    const activeList = cloneDeep(this.watchlists[activeListId]);
    if (activeList && !activeList?.fetchedItems && activeList.watchlistId) {
      this.getWatchlistItems(activeList.watchlistId).then(watchlistItems => {
        this.tvApiInstance.updateList(activeListId, watchlistItems);
        this.watchlists[activeListId] = {
          ...activeList,
          items: watchlistItems,
          fetchedItems: true,
        };
      });
    }

    window.localStorage.setItem(
      ACTIVE_WATCHLIST_ID,
      JSON.stringify({
        id: activeListId,
        title: activeList ? activeList.name : '',
      }),
    );
  }

  formatExchangeTokenToScripName = async (exchangeTokens: string[]) => {
    const result = await Promise.all(
      exchangeTokens.map(async exchangeToken => {
        const scrip = await SearchService.searchByExchangeToken(exchangeToken);

        const tvExchangeToken = FormatService.generateCustomTvExchangeToken(
          scrip?.displayName || '',
          scrip?.exchangeToken || '',
        );

        return tvExchangeToken;
      }),
    );

    return result;
  };
}

export default WatchlistService;
